// pages/LoginPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import backgroundImage from "../assets/images/background.jpg";
import apiClient from "../common/apiClient";
import useCustomSnackbar from "../hooks/useSnackBar";

export default function LoginPage() {
  const navigate = useNavigate();
  const { showSnackbar } = useCustomSnackbar();

  const [formData, setFormData] = useState({
    national_code: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateNationalCode = (code) => {
    if (!/^\d{10}$/.test(code)) return false;
    const check = parseInt(code[9]);
    const sum = code
      .split("")
      .slice(0, 9)
      .reduce((acc, digit, index) => acc + parseInt(digit) * (10 - index), 0);
    const remainder = sum % 11;
    return (
      (remainder < 2 && check === remainder) ||
      (remainder >= 2 && check === 11 - remainder)
    );
  };

  const validatePassword = (password) => {
    if (!password) return "رمز عبور الزامی است";
    return ""; // no error
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "national_code":
        if (!value) error = "کد ملی الزامی است";
        else if (!/^\d{10}$/.test(value)) error = "کد ملی باید ۱۰ رقم باشد";
        else if (!validateNationalCode(value)) error = "کد ملی نامعتبر است";
        break;
      case "password":
        const passwordError = validatePassword(value);
        error = passwordError;
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === "";
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.national_code) newErrors.national_code = "کد ملی الزامی است";
    else if (!/^\d{10}$/.test(formData.national_code))
      newErrors.national_code = "کد ملی باید ۱۰ رقم باشد";
    else if (!validateNationalCode(formData.national_code))
      newErrors.national_code = "کد ملی نامعتبر است";

    if (!formData.password) newErrors.password = "رمز عبور الزامی است";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ national_code: true, password: true });

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const { data } = await apiClient.post("/user/login", {
        national_code: formData.national_code,
        password: formData.password,
      });

      showSnackbar("ورود با موفقیت انجام شد!", "success");

      // Server sets the httpOnly JWT cookie; we only store a non-sensitive
      // UI flag so components can show/hide the logged-in state instantly.
      localStorage.setItem("isLoggedIn", "1");

      navigate('/home');
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-vh-100 position-relative overflow-hidden">
      {/* Background with blur and overlay */}
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(3px)",
          transform: "scale(1.1)",
          zIndex: -2,
        }}
      />

      {/* Dark overlay */}
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          background:
            "linear-gradient(135deg, rgba(67, 67, 67, 0.8), rgba(0, 0, 0, 0.85))",
          zIndex: -1,
        }}
      />

      {/* Main content */}
      <div className="container min-vh-100 d-flex align-items-center justify-content-center py-5">
        <div
          className="col-12 col-md-8 col-lg-5"
          style={{
            opacity: 0,
            transform: "translateY(30px)",
            animation: "fadeInUp 0.8s ease-out forwards",
          }}
        >
          <div
            className="card border-0 shadow-lg"
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: "20px",
              overflow: "hidden",
            }}
          >
            <div className="card-body p-4 p-md-5">
              {/* Header */}
              <div className="text-center mb-4">
                <h1
                  className="fw-bold mb-2"
                  style={{
                    fontSize: "2rem",
                    color: "#2c3e50",
                    opacity: 0,
                    transform: "translateY(20px)",
                    animation: "fadeInUp 0.6s ease-out 0.2s forwards",
                  }}
                >
                  ورود به حساب کاربری
                </h1>
                <p
                  className="text-muted"
                  style={{
                    opacity: 0,
                    transform: "translateY(20px)",
                    animation: "fadeInUp 0.6s ease-out 0.4s forwards",
                  }}
                >
                  لطفاً کد ملی و رمز عبور خود را وارد کنید
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                {/* National Code */}
                <div
                  className="mb-4"
                  style={{
                    opacity: 0,
                    transform: "translateY(20px)",
                    animation: "fadeInUp 0.6s ease-out 0.3s forwards",
                  }}
                >
                  <label
                    htmlFor="national_code"
                    className="form-label fw-semibold text-dark"
                  >
                    کد ملی <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-lg ${
                      touched.national_code && errors.national_code
                        ? "is-invalid"
                        : touched.national_code && !errors.national_code
                        ? "is-valid"
                        : ""
                    }`}
                    id="national_code"
                    name="national_code"
                    value={formData.national_code}
                    onChange={handleChange}
                    onBlur={() => handleBlur("national_code")}
                    placeholder="کد ملی خود را وارد کنید"
                    maxLength="10"
                    style={{
                      borderRadius: "12px",
                      border: "2px solid #e0e0e0",
                      transition: "all 0.3s ease",
                    }}
                  />
                  {touched.national_code && errors.national_code && (
                    <div className="text-danger small mt-2">
                      ⚠️ {errors.national_code}
                    </div>
                  )}
                </div>

                {/* Password with eye icon */}
                <div
                  className="mb-4"
                  style={{
                    opacity: 0,
                    transform: "translateY(20px)",
                    animation: "fadeInUp 0.6s ease-out 0.4s forwards",
                  }}
                >
                  <label
                    htmlFor="password"
                    className="form-label fw-semibold text-dark"
                  >
                    رمز عبور <span className="text-danger">*</span>
                  </label>

                  {/* Wrap input + icon in a relative container */}
                  <div className="position-relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-control form-control-lg ${
                        touched.password && errors.password
                          ? "is-invalid"
                          : touched.password && !errors.password
                          ? "is-valid"
                          : ""
                      }`}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={() => handleBlur("password")}
                      placeholder="رمز عبور خود را وارد کنید"
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e0e0e0",
                        transition: "all 0.3s ease",
                        paddingLeft: "45px",
                      }}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "12px",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#2c3e50",
                        fontSize: "1.1rem",
                      }}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>

                  {/* Error message outside the relative container */}
                  {touched.password && errors.password && (
                    <div className="text-danger small mt-2">
                      ⚠️ {errors.password}
                    </div>
                  )}
                </div>
                {/* Forgot Password Link */}
              <div
                className="d-flex justify-content-start mb-2"
                style={{
                  opacity: 0,
                  transform: "translateY(20px)",
                  animation: "fadeInUp 0.6s ease-out 0.45s forwards",
                }}
              >
                <span
                  onClick={() => navigate("/forgot-password")}
                  className="text-decoration-none fw-semibold"
                  style={{
                    color: "#489d4c",
                    cursor: "pointer",
                  }}
                >
                  رمز عبور را فراموش کرده‌اید؟
                </span>
              </div>

                {/* Submit */}
                <div
                  style={{
                    opacity: 0,
                    transform: "translateY(20px)",
                    animation: "fadeInUp 0.6s ease-out 0.5s forwards",
                  }}
                >
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn-lg w-100 text-white fw-semibold position-relative overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(135deg, #66bb6a 0%, #388e3c 100%)",
                      borderRadius: "12px",
                      border: "none",
                      padding: "14px",
                      fontSize: "1.1rem",
                      transition: "all 0.3s ease",
                      transform: "translateY(0)",
                      boxShadow: "0 4px 15px rgba(102, 187, 106, 0.4)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow =
                          "0 6px 20px rgba(56, 142, 60, 0.6)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 15px rgba(102, 187, 106, 0.4)";
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        در حال ورود...
                      </>
                    ) : (
                      "ورود"
                    )}
                  </button>
                </div>
              </form>

              <p
                className="text-center text-muted mt-4 mb-0 small"
                style={{
                  opacity: 0,
                  transform: "translateY(20px)",
                  animation: "fadeInUp 0.6s ease-out 0.6s forwards",
                }}
              >
                حساب کاربری ندارید؟{" "}
                <span
                  onClick={() => navigate("/signup")}
                  className="text-decoration-none fw-semibold"
                  style={{
                    color: "#489d4c",
                    cursor: "pointer",
                    display: "inline",
                  }}
                >
                  ثبت‌نام کنید
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Inline CSS for animations */}
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
}
